/**
 * Translation for the shared web work tree.
 *
 * The catalogues are the native app's, imported from `@x9/locales`, so a phrase
 * the phone already says is not translated a second time here. That is why the
 * settings surface reuses the `settings` namespace verbatim: native and web are
 * meant to read identically. `tasks` is here for the same reason — `StatusBadge`
 * reads `tasks.status.*`, the labels the phone already puts on the same pill.
 *
 * The resources are bundled rather than fetched, so there is no loading state to
 * design around. But `i18next.init` still resolves on a later tick, and
 * `useSuspense` is off, so a surface that paints on the very first frame would
 * render raw keys and never re-render. Entry points must await `i18nReady`
 * before mounting — every surface used to be shielded by the wait on Firebase
 * auth, which stopped being true once the creation wizards started rendering
 * before any account exists.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enBuckets from '@x9/locales/en/buckets.json';
import enCommon from '@x9/locales/en/common.json';
import enSettings from '@x9/locales/en/settings.json';
import enTasks from '@x9/locales/en/tasks.json';
import enWeb from '@x9/locales/en/web.json';
import ptBuckets from '@x9/locales/pt/buckets.json';
import ptCommon from '@x9/locales/pt/common.json';
import ptSettings from '@x9/locales/pt/settings.json';
import ptTasks from '@x9/locales/pt/tasks.json';
import ptWeb from '@x9/locales/pt/web.json';

import { isMyWebHost } from '../config/webOrigins';

export const LANGUAGE_STORAGE_KEY = 'x9.language';

export type AppLanguage = 'en' | 'pt';

/** Mirrors the native `LocaleContext.SUPPORTED_LANGUAGES` list. */
export const SUPPORTED_LANGUAGES: { code: AppLanguage; nativeName: string }[] = [
  { code: 'en', nativeName: 'English' },
  { code: 'pt', nativeName: 'Português (Brasil)' },
];

const resources = {
  en: { buckets: enBuckets, common: enCommon, settings: enSettings, tasks: enTasks, web: enWeb },
  pt: { buckets: ptBuckets, common: ptCommon, settings: ptSettings, tasks: ptTasks, web: ptWeb },
};

function isSupported(value: string | null | undefined): value is AppLanguage {
  return value === 'en' || value === 'pt';
}

/**
 * Resolution order: an explicit choice the reader has already made, then the
 * host, then the browser.
 *
 * The host outranks the browser because `my.x9.app` is the Brazilian product —
 * a visitor there is expected to read Portuguese regardless of what their
 * browser advertises, which is the behaviour the host checks used to hardcode.
 */
export function detectInitialLanguage(): AppLanguage {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch {
    // Storage is unavailable in some embedded browsers; fall through to the host.
  }

  if (isMyWebHost()) return 'pt';

  const browser = typeof navigator === 'undefined' ? '' : navigator.language;
  return browser.toLowerCase().startsWith('pt') ? 'pt' : 'en';
}

/** Resolves once `t` returns phrases instead of keys. Await before mounting React. */
export const i18nReady: Promise<void> = i18n.isInitialized
  ? Promise.resolve()
  : i18n
      .use(initReactI18next)
      .init({
        resources,
        lng: detectInitialLanguage(),
        fallbackLng: 'en',
        defaultNS: 'web',
        ns: ['web', 'settings', 'common', 'buckets', 'tasks'],
        interpolation: { escapeValue: false },
        react: { useSuspense: false },
      })
      .then(() => undefined);

/** Remembers the choice locally; Firestore is synced separately by the caller. */
export async function setLanguage(code: AppLanguage): Promise<void> {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    // A reader who cannot persist still gets the switch for this session.
  }
  await i18n.changeLanguage(code);
}

export default i18n;
